<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-flex tw-justify-between tw-items-center tw-pt-0">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Locations</h1>
        <base-button v-show="isLocationFiltered === false" @click="add()">
          <template v-slot:icon>
            <plus-icon /> </template
          >New Location
        </base-button>
      </base-content>
    </div>
    <base-content>
      <div class="tw-flex tw-justify-between tw-gap-3">
        <base-text-field v-model="query" ref="searchInput" class="tw-full tw-grow" placeholder="Search for a location by name" size="sm" tabindex="1">
          <template #prefix>
            <search-icon class="tw-h-5 tw-w-5" />
          </template>
        </base-text-field>
        <export-button
          class="tw-my-auto"
          size="sm"
          type="primary"
          label="Export All Locations"
          :filename="`Locations.csv`"
          :requestPayload="exportPayload"
          :url="`/businesses/${businessId}/locations/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
      <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-4">
        <template #location="{item: location}">
          <div class="tw-whitespace-nowrap">{{ location.name }}</div>
        </template>
        <template #address="{item: location}">
          <div class="tw-flex tw-flex-col">
            <div class="tw-whitespace-nowrap">{{ location.line1 }}</div>
            <div v-if="location.line2">{{ location.line2 }}</div>
            <div class="tw-whitespace-nowrap">{{ location.city }}, {{ location.state }} {{ location.zip }}</div>
          </div>
        </template>
        <template #phone="{item: location}">
          <base-phone-number-field :value="location.phoneNumber" />
        </template>
        <template #actions="{item: location}">
          <div class="tw-flex tw-items-center tw-space-x-2">
            <base-button type="flat" @click="edit(location)">
              <template v-slot:icon>
                <edit-icon /> </template
              >Edit
            </base-button>
            <base-button type="flat" @click="editMappings(location)" v-if="showMappings">
              <template v-slot:icon>
                <mapping-icon /> </template
              >Mappings
            </base-button>
            <base-button v-if="isLocationFiltered === false" type="flat" @click="del(location)">
              <template v-slot:icon>
                <trash-icon /> </template
              >Delete
            </base-button>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-60" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-flex-col">
              <base-skeleton-loader class="tw-h-3 tw-w-28" />
              <div class="tw-flex tw-mt-2">
                <base-skeleton-loader class="tw-h-3 tw-w-16" />
                <base-skeleton-loader class="tw-h-3 tw-w-4 tw-mx-2" />
                <base-skeleton-loader class="tw-h-3 tw-w-10" />
              </div>
            </div>
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-18" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-8 tw-w-18" />
              <base-skeleton-loader v-if="isLocationFiltered === false" class="tw-ml-4 tw-h-8 tw-w-18" />
            </div>
          </td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-space-x-6 tw-justify-center">
              <base-skeleton-loader class="tw-h-6 tw-w-12" />
              <base-skeleton-loader class="tw-h-6 tw-w-12" v-if="isLocationFiltered === false" />
              <base-skeleton-loader class="tw-h-6 tw-w-12" v-if="showMappings" />
            </div>
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <business-location-delete-confirmation-modal :businessId="businessId" ref="deleteConfirmationModal" @delete="refresh()" />
    <business-location-slideover :businessId="businessId" ref="slideover" @save="refresh()" />
    <business-location-mappings-slideover ref="mappingsSlideover" :business-id="businessId" v-if="showMappings" />
  </div>
</template>

<script>
import BusinessLocationSlideover from "@/views/business/locations/components/BusinessLocationSlideover";
import BusinessLocationDeleteConfirmationModal from "@/views/business/locations/components/BusinessLocationDeleteConfirmationModal";
import BusinessLocationMappingsSlideover from "@/views/business/locations/components/BusinessLocationMappingsSlideover";
import { mapGetters, mapActions, mapState } from "vuex";
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, MappingIcon } from "@/assets/svgs";
import { debounce } from "debounce";
import ExportButton from "@/components/ExportButton.vue";

const TAKE_KEY = "predictile_locations_take";

export default {
  name: "BusinessLocations",
  components: {
    BusinessLocationSlideover,
    BusinessLocationDeleteConfirmationModal,
    BusinessLocationMappingsSlideover,
    PlusIcon,
    EditIcon,
    TrashIcon,
    SearchIcon,
    MappingIcon,
    ExportButton,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "Location", key: "location", thClass: "" },
        { title: "Address", key: "address", thClass: "" },
        { title: "Phone", key: "phone", thClass: "" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      query: null,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("role", ["isLocationFiltered"]),
    ...mapState("organization", ["organization"]),
    breadcrumbs() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return [{ title: "Settings", onClick: () => this.$router.push(`/businesses/${this.businessId}/business`) }, { title: "Locations" }];
    },
    showMappings() {
      return !!this.organization.usePrescriptionProgramSetting && this.isLocationFiltered === false;
    },
    exportPayload() {
      return { businessId: this.businessId };
    },
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    ...mapActions("location", ["init"]),
    add() {
      this.$refs.slideover.show();
    },
    edit(item) {
      this.$refs.slideover.show(item.businessLocationId);
    },
    editMappings(item) {
      this.$refs.mappingsSlideover.show(item);
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(item.businessLocationId, item.name);
    },
    refresh() {
      this.init();
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/locations`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.items = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load locations`,
            message: `We had a problem retrieving the list of locations. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the list of locations. You may want to reload the page to try again.",
        error: err,
      });
    },
  },
};
</script>
